'use client';

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { defaultLocale, isRtl, localizedHref, type Locale } from './config';
import { getDictionary, type Dictionary } from './index';

export interface LocaleContextValue {
  locale: Locale;
  dict: Dictionary;
  dir: 'ltr' | 'rtl';
  href: (path: string) => string;
}

function buildValue(locale: Locale): LocaleContextValue {
  return {
    locale,
    dict: getDictionary(locale),
    dir: isRtl(locale) ? 'rtl' : 'ltr',
    href: (path: string) => localizedHref(locale, path),
  };
}

// Defaults to English so components using `useLocale()` render correctly
// even in tests/contexts that never mount a `LocaleProvider` (English
// rendering must be visually unchanged, so this keeps that path trivially
// true without forcing every existing test to add a wrapper).
const LocaleContext = createContext<LocaleContextValue>(buildValue(defaultLocale));

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const value = useMemo(() => buildValue(locale), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}
